import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ScrollElements } from '@app/core/forms/directives/scroll-ements';
import { ScrollToInvalidFieldOnFormSubmitDirective } from '@app/core/forms/directives/scroll-to-invalid-field-on-form-submit.directive';
import { applyErrorsToForm } from '@app/core/helpers/apply-errors-to-form';
import { extractHttpErrorResponse } from '@app/core/helpers/extract-http-error-response';
import { NotificationsService } from '@app/core/notifications/services/notifications.service';
import { Country } from '@app/entities/countries/interfaces/country';
import { OrderDetails } from '@app/entities/orders/interfaces/order-details';
import { NavItem } from '@app/interfaces/nav-item';
import { BillingInformationComponent } from '@app/layout/forms/billing-information/billing-information.component';
import { PaymentInformationComponent } from '@app/layout/forms/payment-information/payment-information.component';
import { SignInPage } from '@app/pages/auth/sign-in/sign-in.page';
import {
  AuthSelectors,
  CartSelectors,
  CreateOrder,
  FrontendLogout,
  ListingsSelectors,
  LoadOrderDetails,
  LoadProfile,
  UpadteOrderAmout,
} from '@app/store';
import { deepCopy } from '@app/utils/deep-copy';
import {
  ActionCompletion,
  Actions,
  ofActionCompleted,
  ofActionDispatched,
  Store,
} from '@ngxs/store';
import { merge, Observable, Subject, takeUntil, tap } from 'rxjs';
import { CartSummaryComponent } from '../cart-summary/cart-summary.component';
import { sumPrice } from '../sum-price';

@Component({
  selector: 'app-cart-form',
  templateUrl: './cart-form.component.html',
  styleUrls: ['./cart-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    BillingInformationComponent,
    PaymentInformationComponent,
    ScrollToInvalidFieldOnFormSubmitDirective,
    CartSummaryComponent,
    AsyncPipe,
    SignInPage,
  ],
})
export class CartFormComponent implements OnInit, OnDestroy {
  constructor(
    private store: Store,
    private actions$: Actions,
    private destroyRef: DestroyRef,
    private notificationsService: NotificationsService,
  ) {}

  protected orderDetails$: Observable<OrderDetails | undefined> =
    this.store.select(CartSelectors.orderDetails);

  protected isAuthenticated$: Observable<boolean | undefined> =
    this.store.select(AuthSelectors.isAuthenticated);

  protected navItems: NavItem[] = [{ label: 'Cart' }];

  @ViewChild(ScrollToInvalidFieldOnFormSubmitDirective)
  private scrollDirective!: ScrollToInvalidFieldOnFormSubmitDirective;

  private _billingInformationComponent?: BillingInformationComponent;
  @ViewChild(BillingInformationComponent)
  set billingInformationComponent(
    component: BillingInformationComponent | undefined,
  ) {
    this.replaceBillingInfo$.next();
    this._billingInformationComponent = component;
    if (component) {
      this.addBillingInfo();
    }
  }

  private _paymentInformationComponent?: PaymentInformationComponent;
  @ViewChild(PaymentInformationComponent)
  set paymentInformationComponent(
    component: PaymentInformationComponent | undefined,
  ) {
    this._paymentInformationComponent = component;
    if (component) {
      this.addPayMentInfo();
    }
  }

  protected ScrollElements = ScrollElements;

  protected form: FormGroup = new FormGroup({});

  private logout$ = new Subject<void>();
  private replaceBillingInfo$ = new Subject<void>();

  ngOnInit(): void {
    this.store.dispatch(new LoadOrderDetails());
    this.handleLogout();
    this.handleLogin();
  }

  private handleLogout(): void {
    this.actions$
      .pipe(
        ofActionDispatched(FrontendLogout),
        tap(() => {
          this.logout$.next();
        }),
      )
      .subscribe();
  }

  private handleLogin(): void {
    this.isAuthenticated$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((isAuthenticated: boolean | undefined) => {
          this.form = new FormGroup({});
          if (!isAuthenticated) {
            return;
          }

          this.store.dispatch(new LoadProfile());
          this.handleBackendResponse();
        }),
      )
      .subscribe();
  }

  private handleBackendResponse(): void {
    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.logout$),
        ofActionCompleted(CreateOrder),
        tap((actionCompletion: ActionCompletion) => {
          if (!actionCompletion.result.successful) {
            const errors = extractHttpErrorResponse(actionCompletion);
            let errorMessage: string = '';
            if (errors.detail) {
              errorMessage = errors.detail;
            }
            if (errors.non_field_errors) {
              errorMessage = errors.non_field_errors;
            }
            if (errors.error) {
              errorMessage = errors.error;
            }

            if (errorMessage) {
              this.notificationsService.displayErrorNotification(
                'Error occured!',
                errorMessage,
              );
            }

            applyErrorsToForm(errors, this.form);
            this.scrollDirective.scrollToFirstInvalidControl();
          }
        }),
      )
      .subscribe();
  }

  private addBillingInfo(): void {
    this.form.removeControl('billing_info');
    this.form.addControl(
      'billing_info',
      this._billingInformationComponent!.form,
    );

    (
      (this.form.get('billing_info') as FormGroup).get(
        'country_id',
      ) as FormControl
    ).valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(merge(this.logout$, this.replaceBillingInfo$)),
        tap((countryId: number) => {
          const country = this.store
            .selectSnapshot(ListingsSelectors.countries)
            ?.find((country: Country) => {
              return country.id === countryId;
            });
          if (!country) {
            return;
          }

          this.store.dispatch(new UpadteOrderAmout(country.tax_rate));
        }),
      )
      .subscribe();
  }

  private addPayMentInfo(): void {
    this.form.removeControl('payment_info');
    this.form.addControl(
      'payment_info',
      this._paymentInformationComponent!.form,
    );
  }

  sum(orderDetails: OrderDetails): number {
    return sumPrice(orderDetails);
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    this.store.dispatch(new CreateOrder(deepCopy(this.form.value)));
  }

  ngOnDestroy(): void {
    this.logout$.complete();
    this.replaceBillingInfo$.complete();
  }
}
